import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { useSAL2080_R01Store } from './Store_SAL2080_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { formatDate } from 'src/utils/string/stringUtil';
import { formatNumber } from 'src/utils/number/numberUtils';
import { getDefaultRowIndexColumnConfig, defaultColDef } from 'src/utils/grid/gridStyleUtils';
import { SAL2080_R01Dto, defaultSAL2080_R01Data } from './Dto_SAL2080_R01';
import { CompCdColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { aggridHeaderClass } from 'src/components/common/common';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

interface Props {
	selectedSAL2080_R01List: string[];
	addSelectedSAL2080_R01List: (ids: string[]) => void;
	removeSelectedSAL2080_R01List: (ids: string[]) => void;
}

export const Table_SAL2080_R01: React.FC<Props> = ({
	selectedSAL2080_R01List,
	addSelectedSAL2080_R01List,
	removeSelectedSAL2080_R01List,
}) => {
	const gridRef = useRef<AgGridReact<SAL2080_R01Dto>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	
	const {
		SAL2080_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useSAL2080_R01Store();

	const { compCdList } = useCommonCodeStore();

	const COLUMN_STATE_KEY = 'SAL2080_R01-grid-column-state';

	const codeMaps = useMemo(() => ({
		comp: new Map(compCdList.map(item => [item.compCd, item.compNm])),
	}), [compCdList]);

	// getCellStyle 함수 정의: 공통 hover/선택 스타일 적용
	const { createRowClassRules, getRowId, settingRowIndex, ensureSelectedRow } = useGridSettingContent({
		gridRef,
		key: 'taxBillNo',
		gridData: SAL2080_R01List || [],
		defaultGridData: defaultSAL2080_R01Data,
		selectedRow: selectedSAL2080_R01List,
	});

	const getCellStyle = useCallback((params: any, key: string) => {
		// 하단 고정 행 스타일
		if (params.node.rowPinned === 'bottom') {
			const numericBottomKeys = ['billAmt','vatAmt','netAmt'];
			return { 
				backgroundColor: '#f0f0f0', 
				color: 'black', 
				fontWeight: 'bold',
				textAlign: key === 'taxBillNo' ? 'center' : (numericBottomKeys.includes(key) ? 'right' : 'left'),
			};
		}
		return onlyTouchGridStyle(key, 'taxBillNo')(params);
	}, [hoveredRowIndex, selectedSAL2080_R01List, SAL2080_R01List]);

	// 컬럼별 기본 헤더명 매핑
	const headerNames: Record<string, string> = {
		valueIndex: '',
		taxBillNo: '세금계산서번호',
		taxType: '매입매출구분',
		compCd: '사업장',
		compNm: '회사명',
		customCd: '거래처코드',
		customNm: '거래처명',
		issuedDt: '발행일자',
		dtlDt: '상세일자',
		dtlItemNm: '상세품목명',
		dtlItemSpec: '상세품목규격',
		dtlItemQty: '상세품목수량',
		dtlItemCost: '상세품목단가',
		dtlItemAmt: '상세품목금액',
		billAmt: '공급가액',
		vatAmt: '부가세',
		netAmt: '합계금액',
		remark: '비고',
	};

	const baseColumnDefs: ColDef<SAL2080_R01Dto>[] = useMemo(() => {
		const result: ColDef<SAL2080_R01Dto>[] = [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: '세금계산서번호', colId: 'taxBillNo', field: 'taxBillNo', width: 160,
				cellStyle: (p) => getCellStyle(p, 'taxBillNo'),
				editable: false,
				cellRenderer: (params: { data?: SAL2080_R01Dto }) => params.data?.taxBillNo || '',
			},

			{
				headerName: '사업장', colId: 'compCd', field: 'compCd', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'compCd'),
				cellRenderer: (params: any) => {
					const compNm = codeMaps.comp.get(params.data?.compCd);
					return compNm || params.data?.compCd || '';
				},
				...CompCdColumn.options(compCdList, false, undefined, undefined),
			},
			{
				headerName: '거래처', colId: 'customCd', field: 'customCd', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'customCd'),
				cellRenderer: (params: any) => params.data?.customCd || ''
			},
			{
				headerName: '상호', colId: 'customNm', field: 'customNm' as keyof SAL2080_R01Dto, width: 150,
				cellStyle: (params: any) => getCellStyle(params, 'customNm'),
				cellRenderer: (params: any) => params.data?.customNm || ''
			},
			{
				headerName: '발행일자', colId: 'issuedDt', field: 'issuedDt', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'issuedDt'),
				editable: false,
				cellRenderer: (params: any) => {
					if (params.data?.issuedDt) {
						const date = new Date(params.data.issuedDt);
						if (!isNaN(date.getTime())) {
							return formatDate(date);
						}
					}
					return params.data?.issuedDt || '';
				}
			},
			{
				headerName: '매입매출구분', colId: 'taxType', field: 'taxType', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'taxType'),
				cellRenderer: (params: any) => {
					const taxType = params.data?.taxType;
					return taxType === 'P' ? '매입' : taxType === 'S' ? '매출' : taxType || '';
				}
			},

			{
				headerName: '공급가액', colId: 'billAmt', field: 'billAmt', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'billAmt'),
				type: 'numericColumn',
				valueFormatter: ({ value }) => formatNumber(value || 0),
			},
			{
				headerName: '부가세', colId: 'vatAmt', field: 'vatAmt', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'vatAmt'),
				type: 'numericColumn',
				valueFormatter: ({ value }) => formatNumber(value || 0),
			},
			{
				headerName: '합계금액', colId: 'netAmt', field: 'netAmt', width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'netAmt'),
				type: 'numericColumn',
				valueFormatter: ({ value }) => formatNumber(value || 0),
			},



			{
				headerName: '부가세유형', colId: 'vatType', field: 'vatType', width: 100,
				cellStyle: (params: any) => getCellStyle(params, 'vatType'),
				cellRenderer: (params: any) => {
					const flag = params.data?.vatType;
					return flag === 'A' ? '일반과세' : flag === 'E' ? '영세과세' : flag || '';
				}	
			},

			{
				headerName: '확정여부 Y/N', colId: 'fixedFlag', field: 'fixedFlag', width: 100,
				cellStyle: (params: any) => getCellStyle(params, 'fixedFlag'),
				cellRenderer: (params: any) => {
					// 합계행은 공백 표시
					if (params?.node?.rowPinned === 'bottom') return '';
					const flag = params.data?.fixedFlag;
					return flag === 'Y' ? 'Y' : flag === 'N' ? 'N' : '';
				}
			},

			{
				headerName: '국세청 승인번호', colId: 'ntsApproveNo', field: 'ntsApproveNo', width: 150,
				cellStyle: (params: any) => getCellStyle(params, 'ntsApproveNo'),
				cellRenderer: (params: any) => params.data?.ntsApproveNo || ''
			},

		];
		return result;
	}, [codeMaps.comp, compCdList, getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2080_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2080_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'taxBillNo' as const,
		selectedIds: selectedSAL2080_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => {
			removeSelectedSAL2080_R01List(selectedSAL2080_R01List);
			addSelectedSAL2080_R01List(ids.map(String));
		},
	}), [selectedSAL2080_R01List, addSelectedSAL2080_R01List, removeSelectedSAL2080_R01List]);

	// 하단 고정 행 (합계)
	const pinnedBottomRow = useMemo(() => {
		// 숫자 필드들의 실제 합계 계산
		const totalBillAmt = SAL2080_R01List.reduce((sum, item) => sum + (item.billAmt || 0), 0);
		const totalVatAmt = SAL2080_R01List.reduce((sum, item) => sum + (item.vatAmt || 0), 0);
		const totalNetAmt = SAL2080_R01List.reduce((sum, item) => sum + (item.netAmt || 0), 0);

		return {
			taxBillNo: ` ${SAL2080_R01List.length.toLocaleString()}건`,
			taxType: '',
			compCd: '',
			customCd: '',
			customNm: '',
			issuedDt: null,
			dtlDt: null,
			dtlItemNm: '',
			dtlItemSpec: '',
			dtlItemQty: 0,
			dtlItemCost: 0,
			dtlItemAmt: 0,
			billAmt: totalBillAmt,
			vatAmt: totalVatAmt,
			netAmt: totalNetAmt,
			vatRate: '',
			moneyCd: '',
			exchangeRate: 0,
			vatIncludeFlag: '',
			taxEmpNo: '',
			fixedFlag: '',
			inoutAutoFlag: '',
			ntsApproveNo: '',
			remark: '',
		};
	}, [SAL2080_R01List]);

	// 선택 초기화 함수
	const initSelected = useCallback(() => {
		// 선택 초기화 로직 (필요시 구현)
	}, []);

	useEffect(() => { ensureSelectedRow(); }, [SAL2080_R01List, selectedSAL2080_R01List]);

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<GridContextWrapper<SAL2080_R01Dto>
				enableCellCalculation
				gridRef={gridRef}
				gridWrapperRef={gridWrapperRef}
				paddedGridData={SAL2080_R01List}
				initSelected={initSelected}
				defaultRowData={defaultSAL2080_R01Data}
				baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				enableExcelPaste={false}
				enableDataHistory={false}
				menuItems={undefined}
			>
				<Grid_Touch<SAL2080_R01Dto>
					ref={gridRef}
					rowData={SAL2080_R01List}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowId}
					onGridReady={(params: GridReadyEvent) => {
					}}
					shiftRangeSelection={shiftRangeSelectionConfig}
					suppressRowClickSelection={true}
					animateRows={true}
					enableCellTextSelection={true}
					ensureDomOrder={false}
					pinnedBottomRowData={[pinnedBottomRow]}
					rowClassRules={createRowClassRules<SAL2080_R01Dto>({
						idKey: 'taxBillNo',
						selectedList: selectedSAL2080_R01List,
						deletedList: [],
					})}
					onCellKeyDown={undefined}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
				/>
			</GridContextWrapper>
		</div>
	);
};
